import {
  isSupportTicketStatus,
  type SupportTicketListItemResponse,
  type SupportTicketStatus,
} from "@redotech/effect-api-shared/models/shopper-ai/conversation/support-ticket";
import {
  getSupportTicketClientEvaluation,
  resolveSupportTicketFilterName,
  type SupportTicketFilterName,
  SupportTicketFilterNames,
  UNASSIGNED_ASSIGNEE_SENTINEL,
} from "@redotech/effect-api-shared/schema/support-ticket-filters";
import { SPAM_TAG } from "@redotech/redo-model/conversation-tags/conversation-tag-record";
import type { ArrayStringFilterData } from "@redotech/redo-model/views/advanced-filters/array-string-filter";
import { ArrayStringFilterOperator } from "@redotech/redo-model/views/advanced-filters/array-string-filter";
import type { ExistsFilterData } from "@redotech/redo-model/views/advanced-filters/exists-filter";
import { ExistsFilterOperator } from "@redotech/redo-model/views/advanced-filters/exists-filter";
import {
  AdvancedFilterType,
  FilterOperator,
  isFilterGroup,
} from "@redotech/redo-model/views/advanced-filters/generic-advanced-filter-data";
import type { NumberFilterData } from "@redotech/redo-model/views/advanced-filters/number-filter";
import { NumberFilterOperator } from "@redotech/redo-model/views/advanced-filters/number-filter";
import type {
  AdvancedFilterData,
  FilterGroup,
} from "@redotech/redo-model/views/views";
import type { Ref } from "@tanstack/react-db";
import {
  and,
  eq,
  gt,
  gte,
  inArray,
  isNull,
  lt,
  lte,
  not,
  or,
} from "@tanstack/react-db";

type TanstackExpression = ReturnType<typeof eq>;
type TanstackExpressionBuilder = (
  filter: AdvancedFilterData,
  supportTicketRef: Ref<SupportTicketListItemResponse>,
) => TanstackExpression | null;

/**
 * The ref type for a non-object field T (leaf node in the TanStack ref proxy tree).
 * Extracts the same type that `Ref<{ _: T }>['_']` produces at runtime.
 */
type FieldRef<T> = Ref<{ _: T }>["_"];

function isArrayStringFilter(
  filter: AdvancedFilterData,
): filter is ArrayStringFilterData {
  return filter.type === AdvancedFilterType.ARRAY_TO_STRING;
}

function isExistsFilter(
  filter: AdvancedFilterData,
): filter is ExistsFilterData {
  return filter.type === AdvancedFilterType.EXISTS;
}

function isNumberFilter(
  filter: AdvancedFilterData,
): filter is NumberFilterData {
  return filter.type === AdvancedFilterType.NUMBER;
}

/**
 * Combines an array of expressions with AND or OR, returning null for empty.
 */
function combineExpressions(
  expressions: TanstackExpression[],
  operator: FilterOperator,
): TanstackExpression | null {
  if (expressions.length === 0) {
    return null;
  }
  return expressions.reduce((acc, expr) =>
    operator === FilterOperator.AND ? and(acc, expr) : or(acc, expr),
  );
}

/**
 * Builds a TanStack expression that checks if any of the given values
 * exist in an array field on the support ticket.
 */
function arrayFieldContainsAny<T extends string>(
  arrayRef: FieldRef<readonly T[]>,
  values: T[],
): TanstackExpression | null {
  if (values.length === 0) {
    return null;
  }
  if (values.length === 1) {
    return inArray(values[0], arrayRef);
  }
  const expressions = values.map((v) => inArray(v, arrayRef));
  return or(expressions[0], expressions[1], ...expressions.slice(2));
}

/**
 * Builds a TanStack expression that checks if all of the given values
 * exist in an array field on the support ticket.
 */
function arrayFieldContainsAll<T extends string>(
  arrayRef: FieldRef<readonly T[]>,
  values: T[],
): TanstackExpression | null {
  if (values.length === 0) {
    return null;
  }
  if (values.length === 1) {
    return inArray(values[0], arrayRef);
  }
  const expressions = values.map((v) => inArray(v, arrayRef));
  return and(expressions[0], expressions[1], ...expressions.slice(2));
}

/**
 * Builds a TanStack expression for a numeric filter against a number field.
 */
function numberFilterToExpression(
  filter: AdvancedFilterData,
  fieldRef: FieldRef<number>,
): TanstackExpression | null {
  if (!isNumberFilter(filter) || !filter.value) {
    return null;
  }
  const numbers = [...filter.value].sort((a, b) => a - b);
  const firstNumber = numbers[0];
  if (firstNumber === undefined) {
    return null;
  }
  switch (filter.operator) {
    case NumberFilterOperator.EQUAL:
      return eq(fieldRef, firstNumber);
    case NumberFilterOperator.GREATER_THAN:
      return gt(fieldRef, firstNumber);
    case NumberFilterOperator.LESS_THAN:
      return lt(fieldRef, firstNumber);
    case NumberFilterOperator.BETWEEN: {
      if (numbers.length !== 2) {
        return null;
      }
      const secondNumber = numbers[1];
      if (secondNumber === undefined) {
        return null;
      }
      return and(gte(fieldRef, firstNumber), lte(fieldRef, secondNumber));
    }
    default:
      return null;
  }
}

function effectiveStatusToExpression(
  status: SupportTicketStatus,
  supportTicketRef: Ref<SupportTicketListItemResponse>,
  now: Date,
): TanstackExpression {
  switch (status) {
    case "OPEN":
      return and(
        eq(supportTicketRef.status, "OPEN"),
        or(
          isNull(supportTicketRef.snoozedUntil),
          lte(supportTicketRef.snoozedUntil, now),
        ),
      );
    case "SNOOZED":
      return or(
        eq(supportTicketRef.status, "SNOOZED"),
        and(
          eq(supportTicketRef.status, "OPEN"),
          gt(supportTicketRef.snoozedUntil, now),
        ),
      );
    case "CLOSED":
    case "IN_PROGRESS":
    case "HIDDEN":
    case "ARCHIVED":
    case "DELETED":
    case "DRAFT":
      return eq(supportTicketRef.status, status);
  }
  const _exhaustive: never = status;
  return _exhaustive;
}

function impossibleStatusExpression(
  supportTicketRef: Ref<SupportTicketListItemResponse>,
): TanstackExpression {
  return and(
    eq(supportTicketRef.status, "OPEN"),
    eq(supportTicketRef.status, "CLOSED"),
  );
}

function statusValueToExpression(
  status: string,
  supportTicketRef: Ref<SupportTicketListItemResponse>,
  now: Date,
): TanstackExpression {
  return isSupportTicketStatus(status)
    ? effectiveStatusToExpression(status, supportTicketRef, now)
    : impossibleStatusExpression(supportTicketRef);
}

function statusFilterToExpression(
  filter: ArrayStringFilterData,
  supportTicketRef: Ref<SupportTicketListItemResponse>,
): TanstackExpression | null {
  const values = filter.value ?? [];
  if (values.length === 0) {
    return null;
  }

  const now = new Date();
  const expressions = values.map((status) =>
    statusValueToExpression(status, supportTicketRef, now),
  );

  switch (filter.operator) {
    case ArrayStringFilterOperator.ANY_OF:
      return combineExpressions(expressions, FilterOperator.OR);
    case ArrayStringFilterOperator.NONE_OF: {
      const expression = combineExpressions(expressions, FilterOperator.OR);
      return expression ? not(expression) : null;
    }
    case ArrayStringFilterOperator.ALL_OF:
      return combineExpressions(expressions, FilterOperator.AND);
    default:
      return null;
  }
}

const serverOnlyTanstackExpressionBuilder: TanstackExpressionBuilder = () =>
  null;

const supportTicketTanstackExpressionBuilders = {
  [SupportTicketFilterNames.STATUS]: (filter, supportTicketRef) => {
    if (!isArrayStringFilter(filter)) {
      return null;
    }
    return statusFilterToExpression(filter, supportTicketRef);
  },
  [SupportTicketFilterNames.ASSIGNEES]: (filter, supportTicketRef) => {
    if (!isArrayStringFilter(filter)) {
      return null;
    }

    const values = filter.value ?? [];
    if (values.length === 0) {
      return null;
    }

    const sentinelValues = values.filter(
      (v) => v === UNASSIGNED_ASSIGNEE_SENTINEL,
    );
    const userValues = values.filter((v) => v !== UNASSIGNED_ASSIGNEE_SENTINEL);

    const hasSentinel = sentinelValues.length > 0;
    const hasUsers = userValues.length > 0;

    if (filter.operator === ArrayStringFilterOperator.ANY_OF) {
      if (hasSentinel && hasUsers) {
        const nullExpr = isNull(supportTicketRef.mongoAssigneeId);
        const usersExpr =
          userValues.length === 1
            ? eq(supportTicketRef.mongoAssigneeId, userValues[0])
            : inArray(supportTicketRef.mongoAssigneeId, userValues);
        return or(nullExpr, usersExpr);
      } else if (hasSentinel) {
        return isNull(supportTicketRef.mongoAssigneeId);
      } else if (hasUsers) {
        return userValues.length === 1
          ? eq(supportTicketRef.mongoAssigneeId, userValues[0])
          : inArray(supportTicketRef.mongoAssigneeId, userValues);
      }
      return null;
    }

    if (filter.operator === ArrayStringFilterOperator.NONE_OF) {
      if (hasSentinel && hasUsers) {
        const notNullExpr = not(isNull(supportTicketRef.mongoAssigneeId));
        const notUsersExpr =
          userValues.length === 1
            ? not(eq(supportTicketRef.mongoAssigneeId, userValues[0]))
            : not(inArray(supportTicketRef.mongoAssigneeId, userValues));
        return and(notNullExpr, notUsersExpr);
      } else if (hasSentinel) {
        return not(isNull(supportTicketRef.mongoAssigneeId));
      } else if (hasUsers) {
        return userValues.length === 1
          ? not(eq(supportTicketRef.mongoAssigneeId, userValues[0]))
          : not(inArray(supportTicketRef.mongoAssigneeId, userValues));
      }
      return null;
    }

    return null;
  },
  [SupportTicketFilterNames.CHANNELS]: (filter, supportTicketRef) => {
    if (!isArrayStringFilter(filter)) {
      return null;
    }
    const channelValues = filter.value ?? [];

    if (channelValues.length === 0) {
      return null;
    }

    switch (filter.operator) {
      case ArrayStringFilterOperator.ANY_OF:
        return arrayFieldContainsAny(supportTicketRef.channels, channelValues);
      case ArrayStringFilterOperator.NONE_OF: {
        const anyExpr = arrayFieldContainsAny(
          supportTicketRef.channels,
          channelValues,
        );
        return anyExpr ? not(anyExpr) : null;
      }
      default:
        return null;
    }
  },
  [SupportTicketFilterNames.SUPPORT_TICKET_TAGS]: (
    filter,
    supportTicketRef,
  ) => {
    if (!isArrayStringFilter(filter)) {
      return null;
    }
    const tagValues = filter.value ?? [];

    if (tagValues.length === 0) {
      return null;
    }

    // SPAM_TAG is a tag name ("spam"), not an ID. Client-side filtering would
    // compare the name against tagIds (which are IDs), causing a mismatch.
    // Skip client-side filtering for spam tag and let the server handle it.
    if (tagValues.includes(SPAM_TAG)) {
      return null;
    }

    switch (filter.operator) {
      case ArrayStringFilterOperator.ANY_OF:
        return arrayFieldContainsAny(supportTicketRef.tagIds, tagValues);
      case ArrayStringFilterOperator.ALL_OF:
        return arrayFieldContainsAll(supportTicketRef.tagIds, tagValues);
      case ArrayStringFilterOperator.NONE_OF: {
        const anyExpr = arrayFieldContainsAny(
          supportTicketRef.tagIds,
          tagValues,
        );
        return anyExpr ? not(anyExpr) : null;
      }
      default:
        return null;
    }
  },
  [SupportTicketFilterNames.CUSTOMER_TAGS]: serverOnlyTanstackExpressionBuilder,
  [SupportTicketFilterNames.MENTIONS]: serverOnlyTanstackExpressionBuilder,
  [SupportTicketFilterNames.READ_AT]: (filter, supportTicketRef) => {
    if (!isExistsFilter(filter)) {
      return null;
    }
    const value = filter.value;
    if (value == null) {
      return null;
    }

    // The EXISTS filter has both an operator and a value (boolean).
    // EXISTS + true = field is not null
    // EXISTS + false = field is null
    // NOT_EXISTS + true = field is null
    // NOT_EXISTS + false = field is not null
    switch (filter.operator) {
      case ExistsFilterOperator.EXISTS:
        return value
          ? not(isNull(supportTicketRef.readAt))
          : isNull(supportTicketRef.readAt);
      case ExistsFilterOperator.NOT_EXISTS:
        return value
          ? isNull(supportTicketRef.readAt)
          : not(isNull(supportTicketRef.readAt));
      default:
        return null;
    }
  },
  [SupportTicketFilterNames.WORDS]: serverOnlyTanstackExpressionBuilder,
  [SupportTicketFilterNames.CREATED_AT]: serverOnlyTanstackExpressionBuilder,
  [SupportTicketFilterNames.CLOSED_AT]: serverOnlyTanstackExpressionBuilder,
  [SupportTicketFilterNames.LAST_RESPONSE_AT]:
    serverOnlyTanstackExpressionBuilder,
  [SupportTicketFilterNames.LAST_CUSTOMER_MESSAGE_AT]:
    serverOnlyTanstackExpressionBuilder,
  [SupportTicketFilterNames.LAST_MERCHANT_MESSAGE_AT]:
    serverOnlyTanstackExpressionBuilder,
  [SupportTicketFilterNames.CUSTOMER_NAME]: serverOnlyTanstackExpressionBuilder,
  [SupportTicketFilterNames.PARTICIPANT_KEYS]:
    serverOnlyTanstackExpressionBuilder,
  [SupportTicketFilterNames.PRIMARY_PARTICIPANT_KEYS]:
    serverOnlyTanstackExpressionBuilder,
  [SupportTicketFilterNames.SHOPPER_PARTICIPANT_IDS]:
    serverOnlyTanstackExpressionBuilder,
  [SupportTicketFilterNames.MERCHANT_USER_PARTICIPANT_IDS]:
    serverOnlyTanstackExpressionBuilder,
  [SupportTicketFilterNames.REDO_USER_PARTICIPANT_IDS]:
    serverOnlyTanstackExpressionBuilder,
  [SupportTicketFilterNames.AGENT_PARTICIPANT_IDS]:
    serverOnlyTanstackExpressionBuilder,
  [SupportTicketFilterNames.SYSTEM_PARTICIPANT_KEYS]:
    serverOnlyTanstackExpressionBuilder,
  [SupportTicketFilterNames.NUM_DRAFTS]: (filter, supportTicketRef) =>
    numberFilterToExpression(filter, supportTicketRef.numDrafts),
  [SupportTicketFilterNames.NUM_SENT_MESSAGES]: (filter, supportTicketRef) =>
    numberFilterToExpression(filter, supportTicketRef.numSentMessages),
  [SupportTicketFilterNames.NUM_OUTBOUND_MESSAGES]: (
    filter,
    supportTicketRef,
  ) => numberFilterToExpression(filter, supportTicketRef.numOutboundMessages),
  [SupportTicketFilterNames.NUM_CUSTOMER_MESSAGES]: (
    filter,
    supportTicketRef,
  ) => numberFilterToExpression(filter, supportTicketRef.numCustomerMessages),
  [SupportTicketFilterNames.NUM_MERCHANT_SENT_MESSAGES]: (
    filter,
    supportTicketRef,
  ) =>
    numberFilterToExpression(filter, supportTicketRef.numMerchantSentMessages),
  [SupportTicketFilterNames.NUM_AI_SENT_MESSAGES]: (filter, supportTicketRef) =>
    numberFilterToExpression(filter, supportTicketRef.numAiSentMessages),
  [SupportTicketFilterNames.NUM_AUTO_REPLY_SENT_MESSAGES]: (
    filter,
    supportTicketRef,
  ) =>
    numberFilterToExpression(filter, supportTicketRef.numAutoReplySentMessages),
  [SupportTicketFilterNames.NUM_SYSTEM_SENT_MESSAGES]: (
    filter,
    supportTicketRef,
  ) => numberFilterToExpression(filter, supportTicketRef.numSystemSentMessages),
  [SupportTicketFilterNames.NUM_INTERNAL_NOTES]: (filter, supportTicketRef) =>
    numberFilterToExpression(filter, supportTicketRef.numInternalNotes),
  [SupportTicketFilterNames.ASSIGNEE_TYPE]: serverOnlyTanstackExpressionBuilder,
  [SupportTicketFilterNames.TEXT_CONCIERGE_CONVERSATION_IDS]:
    serverOnlyTanstackExpressionBuilder,
} satisfies Record<SupportTicketFilterName, TanstackExpressionBuilder>;

function getSupportTicketTanstackExpressionBuilder(
  name: string,
): TanstackExpressionBuilder | null {
  const filterName = resolveSupportTicketFilterName(name);
  if (!filterName) {
    return null;
  }
  return supportTicketTanstackExpressionBuilders[filterName];
}

/**
 * Converts a single AdvancedFilterData to a TanStack DB expression.
 *
 * Note: Only handles filters that can be applied client-side to TanStack DB.
 * Complex filters (date ranges, tags requiring joins, etc.) return null
 * and are handled server-side via OpenSearch.
 */
export function advancedFilterToTanstackExpression(
  filter: AdvancedFilterData,
  supportTicketRef: Ref<SupportTicketListItemResponse>,
): TanstackExpression | null {
  if (getSupportTicketClientEvaluation(filter.name) !== "client") {
    return null;
  }

  return (
    getSupportTicketTanstackExpressionBuilder(filter.name)?.(
      filter,
      supportTicketRef,
    ) ?? null
  );
}

/**
 * Converts a FilterGroup to a TanStack DB expression, handling AND/OR logic.
 *
 * Only filters that can be applied client-side will be included. Server-only
 * clauses are skipped for AND groups, while mixed OR groups return null when
 * filtering a partial branch would hide rows returned by OpenSearch.
 */
export function filterGroupToTanstackExpression(
  group: FilterGroup,
  supportTicketRef: Ref<SupportTicketListItemResponse>,
): TanstackExpression | null {
  interface BuildResult {
    expression: TanstackExpression | null;
    complete: boolean;
    safe: boolean;
  }

  const buildGroupExpression = (currentGroup: FilterGroup): BuildResult => {
    const results = currentGroup.clauses.map((clause): BuildResult => {
      if (isFilterGroup(clause)) {
        return buildGroupExpression(clause);
      }

      const expression = advancedFilterToTanstackExpression(
        clause,
        supportTicketRef,
      );
      return expression === null
        ? { expression: null, complete: false, safe: false }
        : { expression, complete: true, safe: true };
    });

    if (currentGroup.operator === FilterOperator.OR) {
      if (results.some((result) => !result.safe)) {
        return { expression: null, complete: false, safe: false };
      }

      const expressions = results.flatMap((result) =>
        result.expression === null ? [] : [result.expression],
      );
      const expression = combineExpressions(expressions, currentGroup.operator);
      return {
        expression,
        complete: results.every((result) => result.complete),
        safe: expression !== null,
      };
    }

    const expressions = results.flatMap((result) =>
      result.expression === null ? [] : [result.expression],
    );
    const expression = combineExpressions(expressions, currentGroup.operator);
    return {
      expression,
      complete: results.every((result) => result.complete),
      safe: expression !== null,
    };
  };

  return buildGroupExpression(group).expression;
}

/**
 * Converts a flat array of AdvancedFilterData to a TanStack DB expression.
 * All filters are combined with AND logic.
 *
 * This is a convenience function for when you don't have a FilterGroup.
 */
export function filtersToTanstackExpression(
  filters: AdvancedFilterData[],
  supportTicketRef: Ref<SupportTicketListItemResponse>,
): TanstackExpression | null {
  const expressions: TanstackExpression[] = [];

  for (const filter of filters) {
    const expr = advancedFilterToTanstackExpression(filter, supportTicketRef);
    if (expr !== null) {
      expressions.push(expr);
    }
  }

  return combineExpressions(expressions, FilterOperator.AND);
}
